import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import {
  ConsentPreferences,
  readStoredConsent,
  writeStoredConsent,
} from '../utils/consentStorage';

interface ConsentContextType {
  hasDecided: boolean;
  marketing: boolean;
  acceptAll: () => void;
  rejectAll: () => void;
  savePreferences: (prefs: ConsentPreferences) => void;
  hasMarketingConsent: () => boolean;
  openPreferences: () => void;
  closePreferences: () => void;
  isPreferencesOpen: boolean;
  showBanner: boolean;
}

const ConsentContext = createContext<ConsentContextType | undefined>(undefined);

export const useConsent = (): ConsentContextType => {
  const context = useContext(ConsentContext);
  if (context === undefined) {
    throw new Error('useConsent must be used within a ConsentProvider');
  }
  return context;
};

export const ConsentProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const initialStored = readStoredConsent();
  const [marketing, setMarketing] = useState(initialStored?.marketing ?? false);
  const [hasDecided, setHasDecided] = useState(initialStored !== null);
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);

  const persist = useCallback((prefs: ConsentPreferences) => {
    writeStoredConsent(prefs);
    setMarketing(prefs.marketing);
    setHasDecided(true);
    setIsPreferencesOpen(false);
  }, []);

  const acceptAll = useCallback(() => {
    persist({ marketing: true });
  }, [persist]);

  const rejectAll = useCallback(() => {
    persist({ marketing: false });
  }, [persist]);

  const savePreferences = useCallback(
    (prefs: ConsentPreferences) => {
      persist(prefs);
    },
    [persist]
  );

  const hasMarketingConsent = useCallback(() => marketing, [marketing]);

  const openPreferences = useCallback(() => {
    setIsPreferencesOpen(true);
  }, []);

  const closePreferences = useCallback(() => {
    setIsPreferencesOpen(false);
  }, []);

  const showBanner = !hasDecided;

  const value = useMemo(
    () => ({
      hasDecided,
      marketing,
      acceptAll,
      rejectAll,
      savePreferences,
      hasMarketingConsent,
      openPreferences,
      closePreferences,
      isPreferencesOpen,
      showBanner,
    }),
    [
      hasDecided,
      marketing,
      acceptAll,
      rejectAll,
      savePreferences,
      hasMarketingConsent,
      openPreferences,
      closePreferences,
      isPreferencesOpen,
      showBanner,
    ]
  );

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
};
